import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RotateCcw } from 'lucide-react';
import { adminService } from '../services/admin-service';
import type { ResumenDelReinicio } from '../types/admin.types';

/** El texto que decidió Sebastián (decisión 2, 3-nov). */
export const TEXTO_DEL_REINICIO =
  'Se archivan los productos y el recorrido empieza de nuevo. La persona entra con la misma cuenta.';

/** Qué pasa al reiniciar, a partir del ensayo del servidor. Nunca habla de borrar: no se borra nada. */
function detalleDelReinicio(r: ResumenDelReinicio): string {
  const productos =
    r.productosPropios === 0
      ? 'No hay productos propios para archivar'
      : r.productosPropios === 1
        ? 'Se archiva 1 producto propio'
        : `Se archivan ${r.productosPropios} productos propios`;
  const restaurables =
    r.productosPropios > 0
      ? ' (con sus vínculos e historial: se pueden restaurar desde Mis productos)'
      : '';
  const misiones = `las misiones vuelven a cero${
    r.misionesHechas > 0 ? ` (había ${r.misionesHechas} cumplidas)` : ''
  }`;
  const ejemplos =
    r.ejemplosArchivados > 0
      ? ` También vuelven ${r.ejemplosArchivados} ${
          r.ejemplosArchivados === 1
            ? 'producto de ejemplo que estaba archivado'
            : 'productos de ejemplo que estaban archivados'
        }.`
      : '';
  return (
    `${productos}${restaurables} y ${misiones}.${ejemplos}` +
    ' Se conservan las tiendas, lo leído de ellas, los vínculos, las reglas y las estrategias; no cambian la persona, su cuenta ni el vencimiento.'
  );
}

/**
 * Reiniciar una empresa demo (Ronda Z; decisión 2 de Sebastián, 3-nov): archiva sus productos propios y el
 * recorrido empieza de nuevo. Primero muestra qué pasa (un ensayo en el servidor), después pide escribir el
 * nombre de la empresa. Si los ejemplos quedaron incompletos, el padre lo avisa: el reinicio ya se hizo.
 */
export function ReiniciarDemoDialog({
  empresa,
  onClose,
  onReiniciada,
}: {
  empresa: { id: string; name: string } | null;
  onClose: () => void;
  /** `completa: false` = los productos de ejemplo no quedaron todos. */
  onReiniciada: (resultado: { completa: boolean; archivados: number }) => void;
}) {
  const [resumen, setResumen] = useState<ResumenDelReinicio | null>(null);
  const [revisando, setRevisando] = useState(false);
  const [confirmacion, setConfirmacion] = useState('');
  const [reiniciando, setReiniciando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setResumen(null);
    setConfirmacion('');
    setError(null);
    if (!empresa) return;
    let activo = true;
    setRevisando(true);
    adminService
      .revisarReinicioDeDemo(empresa.id)
      .then((r) => {
        if (activo) setResumen(r.resumen);
      })
      .catch((err) => {
        if (activo) setError(err?.response?.data?.error ?? 'No se pudo revisar la empresa.');
      })
      .finally(() => {
        if (activo) setRevisando(false);
      });
    return () => {
      activo = false;
    };
  }, [empresa]);

  const puedeConfirmar =
    !!empresa && !!resumen && !revisando && confirmacion.trim() === empresa.name.trim();

  const reiniciar = async () => {
    if (!empresa || !puedeConfirmar) return;
    setReiniciando(true);
    setError(null);
    try {
      const r = await adminService.reiniciarDemo(empresa.id, confirmacion);
      onReiniciada({ completa: r.completa, archivados: r.archivados });
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'No se pudo reiniciar la demo.');
    } finally {
      setReiniciando(false);
    }
  };

  return (
    <Dialog open={!!empresa} onOpenChange={(abierto) => !abierto && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
            Reiniciar demo
          </DialogTitle>
          <DialogDescription>{empresa ? TEXTO_DEL_REINICIO : ' '}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {revisando && <p className="text-sm text-muted-foreground">Revisando la empresa…</p>}

          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          {resumen && (
            <>
              <p className="text-sm">{detalleDelReinicio(resumen)}</p>
              <div className="space-y-1.5">
                <Label htmlFor="confirmacion-reinicio">
                  Escribe el nombre de la empresa para confirmar
                </Label>
                <Input
                  id="confirmacion-reinicio"
                  value={confirmacion}
                  onChange={(e) => setConfirmacion(e.target.value)}
                  placeholder={empresa?.name}
                  autoComplete="off"
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={reiniciar} disabled={!puedeConfirmar || reiniciando}>
            {reiniciando ? 'Reiniciando…' : 'Reiniciar demo'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
